import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, withModifiers as _withModifiers, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "relative min-h-[360px]" }
const _hoisted_2 = {
  key: 0,
  class: "absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/80"
}
const _hoisted_3 = { class: "flex items-center gap-2 text-sm text-gray-700" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", null, "Loading internal admins...", -1)
const _hoisted_5 = { class: "flex items-center justify-between mb-4" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("h2", { class: "text-lg font-semibold text-gray-900" }, "Internal Admins", -1)
const _hoisted_7 = { class: "p-6" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h3", { class: "text-lg font-semibold text-gray-900 mb-4" }, "Create Internal Admin", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-gray-500 mb-4" }, " The new admin will be sent an email to set up their password. ", -1)
const _hoisted_10 = ["onSubmit"]
const _hoisted_11 = /*#__PURE__*/_createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-1" }, "First name", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-1" }, "Last name", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-1" }, "Email", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-1" }, "Phone number (optional)", -1)
const _hoisted_15 = {
  key: 0,
  class: "text-xs text-red-600"
}
const _hoisted_16 = { class: "flex justify-end gap-2 pt-2" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "p-6" }
const _hoisted_20 = { class: "text-lg font-semibold text-gray-900" }
const _hoisted_21 = ["innerHTML"]
const _hoisted_22 = {
  key: 0,
  class: "text-xs text-red-600 mt-2"
}
const _hoisted_23 = { class: "flex justify-end gap-2 mt-6" }
const _hoisted_24 = ["disabled"]
const _hoisted_25 = ["disabled"]

import { computed, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/userStore";
import {
  useInternalAdminStore,
  InternalAdmin,
} from "@/stores/internalAdminStore";
import InternalAdminsTableComponent from "@/modules/main/components/internal-tools/internal-admins/InternalAdminsTableComponent.vue";

type ConfirmKind = "deactivate" | "activate" | "resendInvite" | "resetPassword" | "delete";


export default /*#__PURE__*/_defineComponent({
  __name: 'InternalAdminsView',
  emits: ["goToActivityLogs"],
  setup(__props, { emit }) {



const router = useRouter();
const userStore = useUserStore();
const store = useInternalAdminStore();

const initialPageLoading = ref(true);
const createModalOpen = ref(false);
const creating = ref(false);
const createError = ref("");

const form = reactive({
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
});

const confirmModalOpen = ref(false);
const confirmLoading = ref(false);
const confirmError = ref("");
const confirmKind = ref<ConfirmKind | null>(null);
const targetAdmin = ref<InternalAdmin | null>(null);

const currentUserId = computed(() => userStore.user?._id || "");

const confirmTitle = computed(() => {
  switch (confirmKind.value) {
    case "deactivate": return "Deactivate admin";
    case "activate": return "Activate admin";
    case "resendInvite": return "Resend invitation";
    case "resetPassword": return "Trigger password reset";
    case "delete": return "Delete admin";
    default: return "";
  }
});

const confirmText = computed(() => {
  const name = targetAdmin.value ? `${targetAdmin.value.firstName} ${targetAdmin.value.lastName} (${targetAdmin.value.email})` : "this admin";
  switch (confirmKind.value) {
    case "deactivate": return `Are you sure you want to deactivate ${name}?<br><br><strong>They will not be able to log in.</strong>`;
    case "activate": return `Are you sure you want to re-enable login for ${name}?`;
    case "resendInvite": return `Resend the account setup email to ${name}?`;
    case "resetPassword": return `Send a password reset email to ${name}?`;
    case "delete": return `Permanently delete ${name} from the platform?<br><br><strong>This cannot be undone.</strong>`;
    default: return "";
  }
});

const confirmButtonLabel = computed(() => {
  switch (confirmKind.value) {
    case "deactivate": return "Deactivate";
    case "activate": return "Activate";
    case "resendInvite": return "Resend";
    case "resetPassword": return "Send reset email";
    case "delete": return "Delete";
    default: return "Confirm";
  }
});

const confirmButtonClass = computed(() => {
  if (confirmKind.value === "delete") return "bg-red-600 hover:bg-red-700";
  if (confirmKind.value === "deactivate") return "bg-yellow-600 hover:bg-yellow-700";
  return "bg-blue-600 hover:bg-blue-700";
});

const openConfirm = (kind: ConfirmKind, admin: InternalAdmin) => {
  confirmKind.value = kind;
  targetAdmin.value = admin;
  confirmError.value = "";
  confirmModalOpen.value = true;
};

const confirmDeactivate = (admin: InternalAdmin) => openConfirm("deactivate", admin);
const confirmActivate = (admin: InternalAdmin) => openConfirm("activate", admin);
const confirmResendInvite = (admin: InternalAdmin) => openConfirm("resendInvite", admin);
const confirmResetPassword = (admin: InternalAdmin) => openConfirm("resetPassword", admin);
const confirmDelete = (admin: InternalAdmin) => openConfirm("delete", admin);

const goToActivityLogs = (admin: InternalAdmin) => {
  emit("goToActivityLogs", admin);
};

const runConfirmAction = async () => {
  if (!confirmKind.value || !targetAdmin.value) return;
  confirmLoading.value = true;
  confirmError.value = "";
  try {
    let ok = false;
    const admin = targetAdmin.value;
    switch (confirmKind.value) {
      case "deactivate": ok = await store.deactivate(admin._id); break;
      case "activate": ok = await store.activate(admin._id); break;
      case "resendInvite": ok = await store.resendInvite(admin._id); break;
      case "resetPassword": ok = await store.requestPasswordReset(admin.email); break;
      case "delete": ok = await store.remove(admin._id); break;
    }
    if (ok) {
      confirmModalOpen.value = false;
    } else {
      confirmError.value = "The action failed. Please try again.";
    }
  } finally {
    confirmLoading.value = false;
  }
};

const submitCreate = async () => {
  creating.value = true;
  createError.value = "";
  const result = await store.create({
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim(),
    phoneNumber: form.phoneNumber.trim() || undefined,
  });
  creating.value = false;
  if (result.success) {
    createModalOpen.value = false;
    form.firstName = "";
    form.lastName = "";
    form.email = "";
    form.phoneNumber = "";
    // Re-fetch to ensure list reflects server-side state (including server-generated _id, roles, etc.)
    await store.fetch();
  } else {
    createError.value = result.error || "Failed to create internal admin.";
  }
};

onMounted(async () => {
  if (!userStore.isInternalAdmin) {
    router.push("/main/admin");
    return;
  }
  initialPageLoading.value = true;
  try {
    await store.fetch();
  } finally {
    initialPageLoading.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_spinner_loader = _resolveComponent("spinner-loader")!
  const _component_modal = _resolveComponent("modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (initialPageLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_spinner_loader, { color: 'text-blue-600 dark:text-blue-600' }),
            _hoisted_4
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _hoisted_6,
      _createElementVNode("button", {
        type: "button",
        class: "text-white text-sm font-semibold px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (createModalOpen.value = true))
      }, " + New Internal Admin ")
    ]),
    _createVNode(InternalAdminsTableComponent, {
      admins: _unref(store).sortedAdmins,
      loading: _unref(store).loading,
      actionLoadingId: _unref(store).actionLoadingId,
      currentUserId: currentUserId.value,
      sortKey: _unref(store).sort.key,
      sortDir: _unref(store).sort.dir,
      onSort: _unref(store).setSort,
      onDeactivate: confirmDeactivate,
      onActivate: confirmActivate,
      onResendInvite: confirmResendInvite,
      onResetPassword: confirmResetPassword,
      onDelete: confirmDelete,
      onViewActivity: goToActivityLogs
    }, null, 8, ["admins", "loading", "actionLoadingId", "currentUserId", "sortKey", "sortDir", "onSort"]),
    _createVNode(_component_modal, {
      showCloseButton: true,
      widthClass: 'max-w-lg',
      open: createModalOpen.value,
      onLocalModalOpen: _cache[6] || (_cache[6] = ($event: any) => (createModalOpen.value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_7, [
          _hoisted_8,
          _hoisted_9,
          _createElementVNode("form", {
            class: "space-y-3",
            onSubmit: _withModifiers(submitCreate, ["prevent"])
          }, [
            _createElementVNode("div", null, [
              _hoisted_11,
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.firstName) = $event)),
                type: "text",
                required: "",
                class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              }, null, 512), [
                [_vModelText, form.firstName]
              ])
            ]),
            _createElementVNode("div", null, [
              _hoisted_12,
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.lastName) = $event)),
                type: "text",
                required: "",
                class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              }, null, 512), [
                [_vModelText, form.lastName]
              ])
            ]),
            _createElementVNode("div", null, [
              _hoisted_13,
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.email) = $event)),
                type: "email",
                required: "",
                class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              }, null, 512), [
                [_vModelText, form.email]
              ])
            ]),
            _createElementVNode("div", null, [
              _hoisted_14,
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.phoneNumber) = $event)),
                type: "tel",
                class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              }, null, 512), [
                [_vModelText, form.phoneNumber]
              ])
            ]),
            (createError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(createError.value), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("button", {
                type: "button",
                class: "text-gray-700 text-sm font-semibold border border-gray-300 px-4 py-2 bg-white rounded-lg",
                disabled: creating.value,
                onClick: _cache[5] || (_cache[5] = ($event: any) => (createModalOpen.value = false))
              }, " Cancel ", 8, _hoisted_17),
              _createElementVNode("button", {
                type: "submit",
                class: "text-white text-sm font-semibold px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50",
                disabled: creating.value
              }, _toDisplayString(creating.value ? "Creating..." : "Create"), 9, _hoisted_18)
            ])
          ], 40, _hoisted_10)
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_modal, {
      showCloseButton: true,
      widthClass: 'max-w-md',
      open: confirmModalOpen.value,
      onLocalModalOpen: _cache[8] || (_cache[8] = ($event: any) => (confirmModalOpen.value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_19, [
          _createElementVNode("h3", _hoisted_20, _toDisplayString(confirmTitle.value), 1),
          _createElementVNode("p", {
            class: "text-sm text-gray-600 mt-2",
            innerHTML: confirmText.value
          }, null, 8, _hoisted_21),
          (confirmError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_22, _toDisplayString(confirmError.value), 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_23, [
            _createElementVNode("button", {
              type: "button",
              class: "text-gray-700 text-sm font-semibold border border-gray-300 px-4 py-2 bg-white rounded-lg",
              disabled: confirmLoading.value,
              onClick: _cache[7] || (_cache[7] = ($event: any) => (confirmModalOpen.value = false))
            }, " Cancel ", 8, _hoisted_24),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(['text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50', confirmButtonClass.value]),
              disabled: confirmLoading.value,
              onClick: runConfirmAction
            }, _toDisplayString(confirmLoading.value ? "Working..." : confirmButtonLabel.value), 11, _hoisted_25)
          ])
        ])
      ]),
      _: 1
    }, 8, ["open"])
  ]))
}
}

})